import { useState } from 'react';
import type { ReactNode } from 'react';
import type {
  RoutingConfigPreflightResponse,
  RoutingDrPlanRecord,
  RoutingPreflightRouteSummary,
} from '@nadoutong/contracts';
import { useRunRoutingConfigPreflight, useVerifyRoutingDrPlan } from '../../api/useRoutingDr.ts';
import { coerceErrorMessage, useToast } from '../../components/Toast.tsx';
import { CustomerRef } from '../../components/identity/CustomerRef.tsx';
import { formatLocalTimestamp } from '../../components/formatters.ts';
import { useT } from '../../i18n/index.ts';
import type { I18nKey } from '../../i18n/index.ts';
import {
  Button,
  Chip,
  Drawer,
  EmptyState,
  FormField,
  Select,
  TBody,
  Table,
  Td,
  Th,
  THead,
  Tr,
} from '../../ui/index.ts';
import {
  REPORT_SECTION_PREVIEW_ROWS,
  coverageFindings,
  healthFindings,
  preflightHeadline,
  skuDiffByTraffic,
  unreachableOrgsByTraffic,
} from './preflightReport.ts';

/**
 * The preflight report, read in the order an operator has to act on it
 * (#1421 P3b):
 *
 *  1. the headline verdict and the summary counts,
 *  2. **the orgs a global switch would not reach** — the finding operators
 *     least expect and most need, so it sits directly under the verdict
 *     rather than at the bottom where its wire position would put it,
 *  3. SKUs the candidate does not cover, hard gaps before fall-throughs,
 *  4. targets that would not serve, blocking findings before degradations,
 *  5. the per-SKU before/after diff.
 *
 * Sections 2–5 render worst-first and collapse past
 * {@link REPORT_SECTION_PREVIEW_ROWS} rows behind a "show all" control, with
 * the withheld count stated. Nothing is trimmed silently.
 */

const WINDOW_HOURS_OPTIONS: readonly number[] = [24, 72, 168];

interface Props {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly configId: string | null;
  readonly configLabel: string;
  /** Present when the config is a DR plan — enables "verify and record". */
  readonly plan: RoutingDrPlanRecord | null;
}

export function PreflightReportDrawer({
  open,
  onClose,
  configId,
  configLabel,
  plan,
}: Props): JSX.Element {
  const t = useT();
  const toast = useToast();
  const runPreflight = useRunRoutingConfigPreflight();
  const verifyPlan = useVerifyRoutingDrPlan();
  const [windowHours, setWindowHours] = useState(WINDOW_HOURS_OPTIONS[0] ?? 24);
  const [report, setReport] = useState<RoutingConfigPreflightResponse | null>(null);

  function handleOpenChange(next: boolean): void {
    if (!next) {
      // The report belongs to the config it was run against; keeping it across
      // opens would show one config's findings under another's name.
      setReport(null);
      onClose();
    }
  }

  async function handleRun(): Promise<void> {
    if (configId === null) return;
    try {
      setReport(await runPreflight.mutateAsync({ configId, windowHours }));
    } catch (error) {
      toast.error(coerceErrorMessage(error));
    }
  }

  async function handleVerify(): Promise<void> {
    if (configId === null || plan === null) return;
    try {
      const response = await verifyPlan.mutateAsync({ configId, windowHours });
      setReport(response.preflight);
      toast.success(t('routing_dr.preflight.toast.verified'));
    } catch (error) {
      toast.error(coerceErrorMessage(error));
    }
  }

  const busy = runPreflight.isPending || verifyPlan.isPending;

  return (
    <Drawer
      open={open}
      onOpenChange={handleOpenChange}
      width="xl"
      title={t('routing_dr.preflight.title')}
      description={configLabel}
      closeLabel={t('common.close')}
      testId="routing-dr-preflight-drawer"
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-end gap-3">
          <FormField
            label={t('routing_dr.preflight.window')}
            fieldId="routing-dr-preflight-window"
            className="w-44"
          >
            <Select
              value={String(windowHours)}
              onValueChange={(value) => {
                const parsed = Number.parseInt(value, 10);
                if (Number.isFinite(parsed)) setWindowHours(parsed);
              }}
              options={WINDOW_HOURS_OPTIONS.map((hours) => ({
                value: String(hours),
                label: t('routing_dr.preflight.window_hours', { count: hours }),
              }))}
              testId="routing-dr-preflight-window"
            />
          </FormField>
          <Button
            variant="primary"
            loading={runPreflight.isPending}
            disabled={busy || configId === null}
            onClick={() => void handleRun()}
            data-testid="routing-dr-preflight-run"
          >
            {t('routing_dr.preflight.run')}
          </Button>
          {plan === null ? null : (
            <Button
              loading={verifyPlan.isPending}
              disabled={busy}
              onClick={() => void handleVerify()}
              data-testid="routing-dr-preflight-verify"
            >
              {t('routing_dr.preflight.verify')}
            </Button>
          )}
        </div>
        <p className="m-0 text-dense leading-5 text-paper-low">
          {t('routing_dr.preflight.read_only_hint')}
        </p>

        {report === null ? (
          busy ? (
            <p className="m-0 text-body text-paper-low" data-testid="routing-dr-preflight-loading">
              {t('routing_dr.preflight.running')}
            </p>
          ) : (
            <EmptyState
              title={t('routing_dr.preflight.empty_title')}
              hint={t('routing_dr.preflight.empty_hint')}
              testId="routing-dr-preflight-empty"
            />
          )
        ) : (
          <PreflightReportBody report={report} />
        )}
      </div>
    </Drawer>
  );
}

function PreflightReportBody({
  report,
}: {
  readonly report: RoutingConfigPreflightResponse;
}): JSX.Element {
  const t = useT();
  const headline = preflightHeadline(report);
  const unreachable = unreachableOrgsByTraffic(report.unreachable_orgs);
  const coverage = coverageFindings(report.coverage);
  const health = healthFindings(report.target_health);
  const diff = skuDiffByTraffic(report.sku_diff);
  const headlineTone = headline === 'clean' ? 'ok' : headline === 'changed' ? 'signal' : 'bad';

  return (
    <div className="flex flex-col gap-4" data-testid="routing-dr-preflight-report">
      <div className="flex flex-col gap-2 rounded-lg border border-line-soft bg-well p-3">
        <div className="flex flex-wrap items-center gap-2">
          <Chip tone={headlineTone} testId="routing-dr-preflight-headline">
            {t(`routing_dr.preflight.headline.${headline}`)}
          </Chip>
          <span className="font-mono text-micro text-paper-low">
            {t('routing_dr.preflight.generated_at', {
              at: formatLocalTimestamp(report.generated_at, { seconds: true }),
            })}
          </span>
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1.5">
          <SummaryStat label={t('routing_dr.preflight.stat.uncovered')} value={report.summary.uncovered} tone={report.summary.uncovered > 0 ? 'bad' : 'muted'} testId="routing-dr-stat-uncovered" />
          <SummaryStat label={t('routing_dr.preflight.stat.fall_through')} value={report.summary.fall_through} tone={report.summary.fall_through > 0 ? 'warn' : 'muted'} />
          <SummaryStat label={t('routing_dr.preflight.stat.covered')} value={report.summary.covered} tone="muted" />
          <SummaryStat label={t('routing_dr.preflight.stat.unhealthy_targets')} value={report.summary.unhealthy_targets} tone={report.summary.unhealthy_targets > 0 ? 'bad' : 'muted'} />
          <SummaryStat label={t('routing_dr.preflight.stat.degraded_targets')} value={report.summary.degraded_targets} tone={report.summary.degraded_targets > 0 ? 'warn' : 'muted'} />
          <SummaryStat label={t('routing_dr.preflight.stat.changed_skus')} value={report.summary.changed_skus} tone="muted" />
          <SummaryStat
            label={t('routing_dr.preflight.stat.unreachable_orgs')}
            value={report.summary.unreachable_orgs_total}
            tone={report.summary.unreachable_orgs_total > 0 ? 'warn' : 'muted'}
            testId="routing-dr-stat-unreachable-orgs"
          />
        </div>
        <p className="m-0 text-dense text-paper-low">
          {t('routing_dr.preflight.window_summary', {
            hours: report.window.hours,
            skus: report.window.evaluated_sku_count,
          })}
          {report.window.truncated ? ` · ${t('routing_dr.preflight.window_truncated')}` : ''}
        </p>
        {report.candidate_config.status === 'active' ? null : (
          <p className="m-0 text-dense text-warn" data-testid="routing-dr-preflight-not-active">
            {t('routing_dr.preflight.candidate_not_active')}
          </p>
        )}
      </div>

      {/*
        Placed directly under the verdict on purpose. A global switch that does
        not reach an org is the failure nobody looks for: the switch succeeds,
        the config changes, and that customer keeps serving from a tier the
        operator never touched.
      */}
      <ReportSection
        caption={t('routing_dr.preflight.section.unreachable_orgs')}
        hint={t('routing_dr.preflight.section.unreachable_orgs_hint')}
        count={unreachable.length}
        // The gateway caps this list, and the cap is the one number in the
        // report that can be read as a fleet-wide count when it is not. The
        // correction belongs beside the row count, not two screens down in
        // the caveat block — which still renders, for the operator who reads
        // the report end to end rather than only its top.
        total={report.summary.unreachable_orgs_total}
        emptyLabel={t('routing_dr.preflight.section.unreachable_orgs_none')}
        testId="routing-dr-unreachable-orgs"
        columns={[
          t('routing_dr.preflight.column.org'),
          t('routing_dr.preflight.column.reason'),
          t('routing_dr.preflight.column.skus'),
          t('routing_dr.preflight.column.requests'),
        ]}
        rows={unreachable.map((org) => (
          <Tr key={`${org.org_id}-${org.reason}`} interactive={false}>
            <Td mono>
              <CustomerRef orgId={org.org_id} />
            </Td>
            <Td>{t(`routing_dr.unreachable_reason.${org.reason}`)}</Td>
            <Td mono>
              {org.sku_ids === null
                ? t('routing_dr.preflight.all_skus')
                : org.sku_ids.join(', ')}
            </Td>
            <Td num>{org.requests}</Td>
          </Tr>
        ))}
      />

      <ReportSection
        caption={t('routing_dr.preflight.section.coverage')}
        hint={t('routing_dr.preflight.section.coverage_hint', { covered: report.summary.covered })}
        count={coverage.length}
        emptyLabel={t('routing_dr.preflight.section.coverage_none')}
        testId="routing-dr-coverage"
        columns={[
          t('routing_dr.preflight.column.sku'),
          t('routing_dr.preflight.column.status'),
          t('routing_dr.preflight.column.detail'),
          t('routing_dr.preflight.column.requests'),
        ]}
        rows={coverage.map((row) => (
          <Tr key={`${row.sku_id}-${row.protocol}`} interactive={false}>
            <Td mono>
              {row.sku_id}
              <span className="ml-1.5 text-paper-low">{row.protocol}</span>
            </Td>
            <Td>
              <Chip tone={row.status === 'uncovered' ? 'bad' : 'warn'}>
                {t(`routing_dr.coverage_status.${row.status}`)}
              </Chip>
            </Td>
            <Td>
              {row.status === 'uncovered'
                ? row.uncovered_reason === null
                  ? '—'
                  : t(`routing_dr.uncovered_reason.${row.uncovered_reason}`)
                : row.fallback_tier === null
                  ? '—'
                  : t(`routing_dr.fallback_tier.${row.fallback_tier}`)}
            </Td>
            <Td num>{row.requests}</Td>
          </Tr>
        ))}
      />

      <ReportSection
        caption={t('routing_dr.preflight.section.health')}
        hint={t('routing_dr.preflight.section.health_hint')}
        count={health.length}
        emptyLabel={t('routing_dr.preflight.section.health_none')}
        testId="routing-dr-health"
        columns={[
          t('routing_dr.preflight.column.target'),
          t('routing_dr.preflight.column.status'),
          t('routing_dr.preflight.column.findings'),
          t('routing_dr.preflight.column.sku_count'),
        ]}
        rows={health.map((row) => (
          <Tr
            key={`${row.upstream_id}-${row.account_id}-${row.key_bucket_id}-${row.upstream_channel_id}-${row.capability_id ?? 'unpinned'}`}
            interactive={false}
          >
            <Td mono>
              {row.upstream_id} · {row.account_id} · {row.key_bucket_id}
              <span className="mt-0.5 block text-paper-low">
                {row.upstream_channel_id} · {row.capability_id ?? t('routing_dr.preflight.unpinned')}
              </span>
            </Td>
            <Td>
              <Chip tone={row.healthy ? 'warn' : 'bad'}>
                {row.healthy
                  ? t('routing_dr.health.degraded')
                  : t('routing_dr.health.blocking')}
              </Chip>
            </Td>
            <Td>
              <span className="flex flex-wrap gap-1">
                {row.findings.map((finding) => (
                  <span key={finding} className="text-dense text-paper-mid">
                    {t(`routing_dr.finding.${finding}`)}
                  </span>
                ))}
              </span>
              <span className="mt-0.5 block font-mono text-micro text-paper-low">
                {t('routing_dr.health.breaker', {
                  state: t(`routing_dr.breaker_state.${row.breaker_state}`),
                })}
              </span>
            </Td>
            <Td num>{row.sku_ids.length}</Td>
          </Tr>
        ))}
      />

      <ReportSection
        caption={t('routing_dr.preflight.section.diff')}
        hint={t('routing_dr.preflight.section.diff_hint')}
        count={diff.length}
        emptyLabel={t('routing_dr.preflight.section.diff_none')}
        testId="routing-dr-diff"
        columns={[
          t('routing_dr.preflight.column.sku'),
          t('routing_dr.preflight.column.before'),
          t('routing_dr.preflight.column.after'),
          t('routing_dr.preflight.column.requests'),
        ]}
        rows={diff.map((row) => (
          <Tr key={`${row.sku_id}-${row.protocol}`} interactive={false}>
            <Td mono>
              {row.sku_id}
              <span className="ml-1.5 text-paper-low">{row.protocol}</span>
            </Td>
            <Td mono className="text-paper-low">{formatRouteSummary(row.before)}</Td>
            <Td mono>{formatRouteSummary(row.after)}</Td>
            <Td num>{row.requests}</Td>
          </Tr>
        ))}
      />

      {report.skus_outside_window.length === 0 && report.caveats.length === 0 ? null : (
        <div
          className="flex flex-col gap-1 rounded-lg border border-line-soft bg-well p-3 text-dense text-paper-low"
          data-testid="routing-dr-preflight-caveats"
        >
          {report.skus_outside_window.length > 0 ? (
            <p className="m-0">
              {t('routing_dr.preflight.skus_outside_window', {
                count: report.skus_outside_window.length,
              })}
            </p>
          ) : null}
          {report.caveats.map((caveat) => (
            <p className="m-0" key={caveat}>{t(`routing_dr.caveat.${caveat}`)}</p>
          ))}
        </div>
      )}
    </div>
  );
}

function SummaryStat({
  label,
  value,
  tone,
  testId,
}: {
  readonly label: string;
  readonly value: number;
  readonly tone: 'muted' | 'warn' | 'bad';
  readonly testId?: string;
}): JSX.Element {
  return (
    <span className="flex items-baseline gap-1.5">
      <span className="text-micro font-bold text-paper-low">{label}</span>
      <span
        className={[
          'font-mono text-body font-medium',
          tone === 'bad' ? 'text-bad' : tone === 'warn' ? 'text-warn' : 'text-paper',
        ].join(' ')}
        data-testid={testId}
      >
        {value}
      </span>
    </span>
  );
}

/**
 * One report section. Renders nothing but a stated absence when empty — an
 * operator has to be able to tell "no unreachable orgs" from "this panel
 * forgot to render unreachable orgs".
 */
function ReportSection({
  caption,
  hint,
  count,
  total,
  emptyLabel,
  columns,
  rows,
  testId,
}: {
  readonly caption: string;
  readonly hint: string;
  readonly count: number;
  /**
   * Findings that exist server-side, when the endpoint caps what it returns.
   * Stated inline whenever it exceeds `count`, so the row count can never be
   * mistaken for the whole finding.
   */
  readonly total?: number;
  readonly emptyLabel: string;
  readonly columns: readonly string[];
  readonly rows: readonly ReactNode[];
  readonly testId: string;
}): JSX.Element {
  const t = useT();
  const [expanded, setExpanded] = useState(false);
  const withheld = rows.length - REPORT_SECTION_PREVIEW_ROWS;
  const visible = expanded ? rows : rows.slice(0, REPORT_SECTION_PREVIEW_ROWS);
  const capped = total !== undefined && total > count;

  return (
    <section data-testid={testId} className="flex flex-col gap-1.5">
      <div className="flex flex-wrap items-baseline gap-2">
        <h3 className="m-0 text-section text-paper">{caption}</h3>
        <span className="font-mono text-micro text-paper-low" data-testid={`${testId}-count`}>
          {count}
        </span>
        {capped ? (
          <span className="text-dense text-warn" data-testid={`${testId}-capped`}>
            {t('routing_dr.preflight.section_capped', { shown: count, total: total ?? count })}
          </span>
        ) : null}
        {/*
          A hint that explains the rows is noise above a section that has no
          rows — and worse, "another 12 SKUs are served here and not listed"
          directly above "every SKU is served here" reads as a contradiction.
        */}
        {rows.length === 0 ? null : <span className="text-dense text-paper-low">{hint}</span>}
      </div>
      {rows.length === 0 ? (
        <p className="m-0 text-dense text-paper-mid" data-testid={`${testId}-none`}>
          {emptyLabel}
        </p>
      ) : (
        <>
          <Table>
            <THead>
              <Tr interactive={false}>
                {columns.map((column, index) => (
                  <Th key={column} num={index === columns.length - 1}>{column}</Th>
                ))}
              </Tr>
            </THead>
            <TBody>{visible}</TBody>
          </Table>
          {withheld > 0 && !expanded ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setExpanded(true)}
              data-testid={`${testId}-show-all`}
            >
              {t('routing_dr.preflight.show_all', { count: withheld })}
            </Button>
          ) : null}
        </>
      )}
    </section>
  );
}

/** Winning ladder row, flattened to the identity an operator compares. */
function formatRouteSummary(summary: RoutingPreflightRouteSummary): string {
  if (summary.target_id === null && summary.upstream_id === null) return '—';
  return [
    summary.upstream_id ?? '—',
    summary.account_id ?? '—',
    summary.key_bucket_id ?? '—',
    summary.upstream_channel_id ?? '—',
    summary.capability_id ?? '—',
  ].join(' · ');
}
